"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { RiCloseLine } from "@remixicon/react";
import { cn } from "./utils";

const Drawer = ({ modal = true, ...props }) => (
  <DialogPrimitive.Root modal={modal} {...props} />
);
const DrawerTrigger = DialogPrimitive.Trigger;
const DrawerClose = DialogPrimitive.Close;
const DrawerPortal = DialogPrimitive.Portal;

const DrawerOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out",
      className
    )}
    {...props}
    ref={ref}
  />
));
DrawerOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DrawerContent = React.forwardRef(
  ({ side = "right", className, children, hideCloseButton = false, closeOnOutsideClick = true, overlayClassName, ...props }, ref) => (
    <DrawerPortal>
      <DrawerOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        ref={ref}
        onInteractOutside={(e) => {
          if (!closeOnOutsideClick) {
            e.preventDefault();
            return;
          }
          // Ignore interactions that originated inside a portaled Modal
          // (`data-modal-portal`), a sonner toast (`data-sonner-toast` /
          // `data-sonner-toaster`), or a SweetAlert popup. All three render
          // into document.body, which puts them "outside" the drawer's
          // subtree from Radix's perspective — without this guard, clicking
          // a button in any of them collapses the drawer beneath (and, for
          // the toast case, also unmounts any modal the button was trying
          // to open inside the drawer).
          const target = e.target;
          if (
            typeof target?.closest === 'function' &&
            target.closest('[data-modal-portal="true"], [data-sonner-toast], [data-sonner-toaster]')
          ) {
            e.preventDefault();
            return;
          }
          if (typeof document !== 'undefined' && document.querySelector('.swal2-container')) {
            e.preventDefault();
          }
        }}
        className={cn(
          "fixed z-50 gap-4 bg-white p-6 shadow-lg",
          side === "top" &&
            "inset-x-0 top-0 border-b data-[state=open]:animate-slide-in-from-top data-[state=closed]:animate-slide-out-to-top",
          side === "bottom" &&
            "inset-x-0 bottom-0 border-t data-[state=open]:animate-slide-in-from-bottom data-[state=closed]:animate-slide-out-to-bottom",
          side === "left" &&
            "inset-y-0 left-0 h-full w-3/4 border-r data-[state=open]:animate-slide-in-from-left data-[state=closed]:animate-slide-out-to-left sm:max-w-sm",
          side === "right" &&
            "inset-y-0 right-0 h-full w-full border-l data-[state=open]:animate-slide-in-from-right data-[state=closed]:animate-slide-out-to-right",
          className
        )}
        {...props}
      >
        {children}
        {!hideCloseButton && (
          <DialogPrimitive.Close className="absolute right-6 top-5 p-1 rounded-full border border-gray-200 bg-white text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors focus:outline-none disabled:pointer-events-none">
            <RiCloseLine className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DrawerPortal>
  )
);
DrawerContent.displayName = DialogPrimitive.Content.displayName;

const DrawerHeader = ({ className, ...props }) => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className
    )}
    {...props}
  />
);
DrawerHeader.displayName = "DrawerHeader";

const DrawerFooter = ({ className, ...props }) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
);
DrawerFooter.displayName = "DrawerFooter";

const DrawerTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold text-gray-900", className)}
    {...props}
  />
));
DrawerTitle.displayName = DialogPrimitive.Title.displayName;

const DrawerDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-gray-500", className)}
    {...props}
  />
));
DrawerDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
};
